import { useEffect, useState } from 'react';

const STORAGE_KEY = 'proc-dock-collapsed';
const POLL_INTERVAL_MS = 300;

const readCollapsedFlag = (): boolean => {
  try {
    return sessionStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
};

/**
 * Mirror the dock's collapsed/expanded state into the canvas's local copy so
 * the canvas margin adjusts with the panel width.
 *
 * `ProcessSidePanel` writes the canonical flag to `sessionStorage` whenever
 * the user toggles its rail. The editor page reads that flag to decide how
 * much room to leave the canvas. We listen for both same-tab toggles (by
 * polling on a 300 ms interval — `sessionStorage` doesn't fire `storage`
 * events for same-tab writes) and cross-tab edits (`storage` event) so the
 * canvas always reflects the latest user intent without coupling the panel
 * and the page through props or context.
 */
export const useDockCollapsedSync = (): boolean => {
  const [collapsed, setCollapsed] = useState(readCollapsedFlag);

  useEffect(() => {
    const onStorage = () => setCollapsed(readCollapsedFlag());
    globalThis.addEventListener('storage', onStorage);
    const id = globalThis.setInterval(onStorage, POLL_INTERVAL_MS);
    return () => {
      globalThis.removeEventListener('storage', onStorage);
      globalThis.clearInterval(id);
    };
  }, []);

  return collapsed;
};
